import { useMutation } from '@tanstack/react-query';

import { ModelVersion } from '@verantos-dev/app-state-research-assistant';
import { transformUser } from '@verantos-dev/core-auth';
import { CohortBuilderDefinitionLegacy } from '@verantos-dev/types-cohorts';
import { IVerantosUser } from '@verantos-dev/types-common';

/**
 * Minimal duck-typed socket interface — mirrors the shape used by
 * `useRevertCohortMutation`. Lets this package stay free of a runtime
 * `socket.io-client` dependency so consumers without sockets (mostly tests)
 * can still depend on data-access-cohorts.
 */
export interface AutosaveCohortSocket {
  on: (event: string, listener: (...args: unknown[]) => void) => void;
  off: (event: string, listener: (...args: unknown[]) => void) => void;
  emit: (event: string, ...args: unknown[]) => void;
}

export interface AutosaveCohortInput {
  projectId: string;
  cohortId: string;
  cohortVersionId: string;
  model: ModelVersion;
  cohortDefinition: CohortBuilderDefinitionLegacy;
  user: IVerantosUser;
  socket: AutosaveCohortSocket | null;
  /** Optional ack timeout in milliseconds; defaults to 30s. */
  timeoutMs?: number;
}

/**
 * Canonical TanStack mutation for the cohort-builder autosave. Wraps the
 * existing `publish:uiGeneratedCohort` socket emit + `staged_changes_updated`
 * server broadcast into TanStack's mutation lifecycle so callers get the
 * same `mutateAsync / isPending / isError / error` ergonomics as
 * Save / Process / Revert.
 *
 * The socket is passed in as an argument (rather than read from a hook
 * inside) so the caller controls socket lifecycle and the mutation stays a
 * pure function of its input — same pattern as `useRevertCohortMutation`.
 *
 * Caveat: `staged_changes_updated` is a broadcast that fires for ANY path
 * touching staged state (autosave, save, revert). It does not carry a
 * correlation ID, so this mutation resolves on the next event after emit —
 * best-effort lifecycle, not strict happens-before semantics. Concurrent
 * autosaves or autosave + save races may resolve mutations against a
 * different write than the one they kicked off. Treat the mutation as a
 * lifecycle wrapper, not a correctness guarantee, until a future correlation
 * id lands on the server contract.
 *
 * Unlike Save/Process, this mutation does NOT invalidate the cohort profile
 * cache on success — the BE-broadcast `staged_changes_updated` listener
 * already drives store hydration, and invalidating here would race with it.
 */
export const useAutosaveCohortMutation = () => {
  return useMutation<{ ok: true }, Error, AutosaveCohortInput>({
    mutationFn: ({
      projectId,
      cohortId,
      cohortVersionId,
      model,
      cohortDefinition,
      user,
      socket,
      timeoutMs = 30_000,
    }) => {
      if (!socket) {
        return Promise.reject(new Error('useAutosaveCohortMutation: socket is not connected'));
      }

      return new Promise<{ ok: true }>((resolve, reject) => {
        let settled = false;

        const cleanup = () => {
          socket.off('staged_changes_updated', onStagedChangesUpdated);
          clearTimeout(timer);
        };

        // BE emits `staged_changes_updated` after persisting (or attempting
        // to persist) — see `_handle_ui_edit` in agent_namespace_stateless.
        // On failure paths the BE adds a `saveError` field to the payload so
        // we can reject the mutation with the real cause instead of waiting
        // for the 30s timeout backstop.
        const onStagedChangesUpdated = (...args: unknown[]) => {
          if (settled) return;
          settled = true;
          cleanup();
          const payload = args[0] as { saveError?: string } | undefined;
          if (payload?.saveError) {
            reject(new Error(`useAutosaveCohortMutation: save failed — ${payload.saveError}`));
            return;
          }
          resolve({ ok: true });
        };

        const timer = setTimeout(() => {
          if (settled) return;
          settled = true;
          cleanup();
          reject(
            new Error('useAutosaveCohortMutation: timed out waiting for staged_changes_updated')
          );
        }, timeoutMs);

        socket.on('staged_changes_updated', onStagedChangesUpdated);
        socket.emit('publish', {
          message: 'uiGeneratedCohort',
          model,
          data: {
            projectId,
            cohortId,
            cohortVersionId,
            uiDSL: cohortDefinition,
            userInfo: transformUser(user),
          },
        });
      });
    },
  });
};
